import {catchError, filter, map, switchMap, tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, NavigationExtras, Params, Router} from '@angular/router';
import {get as _get} from 'lodash';
import {AuthService} from '../core/auth/auth.service';
import {DiagramMetaService} from '../diagram-data/diagram-meta.service';
import {environment} from '../../environments/environment';
import {FirebaseDataService} from '../editor-state/firebase-data.service';
import {DiagramJsonHandlerService} from '../diagram-data/diagram-json-handler.service';
import {NavigationService, NavObject} from '../core/navigation/navigation.service';
import {NavigationHistoryService} from '../core/services/navigation-history.service';

import {ChangelogsService} from '../core/services/changelogs.service';
import {VersionsService} from '../core/services/versions.service';
import {PopupManagerService} from '../core/services/popup-manager.service';
import {DiagramFollowService} from '../core/services/diagram-follow.service';
import {ClientEnvService} from '../core/services/client-env.service';
import {SwimlanesService} from '../diagram-interaction/services/swimlanes.service';
import {TrainingsService} from '../core/services/trainings.service';
import {ToolbarStateService} from '../editor-interface/toolbar/toolbar-state.service';
import {ERROR_STATUSES} from '../core/values/error-statuses';
import {DiagramMetaStoreService} from '../diagram-data/diagram-meta-store.service';
import {UserViewService} from '../shared/service/user-view.service';
import {ViewSourceService} from '../shared/service/view-source.service';
import {DiagramMetaInterface} from '../diagram-data/diagram-meta.interface';
import {DIAGRAM_VERSION_STATES_ENUM} from '../editor-interface/shared/models/diagram-version.constant';
import {AuthorisationService} from '../core/services/authorisation.service';
import {firstValueFrom} from 'rxjs';
import {DiagramTypeConfigService} from '../core/services/diagram-type-config.service';

const DIAGRAM_VERSION_NOT_EXIST = 'diagram.version.not.exist';

@Injectable()
export class InitGuard {
  constructor(
    private auth: AuthService,
    private meta: DiagramMetaService,
    private fbDataSvc: FirebaseDataService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private navSvc: NavigationService,
    private changelogsSvc: ChangelogsService,
    private versionsSvc: VersionsService,
    private router: Router,
    private diagramFollowSvc: DiagramFollowService,
    private popupSvc: PopupManagerService,
    private clientEnvSvc: ClientEnvService,
    private swimlanesSvc: SwimlanesService,
    private trainingSvc: TrainingsService,
    private diagramStoreService: DiagramMetaStoreService,
    private toolbarStateService: ToolbarStateService,
    private userViewService: UserViewService,
    private viewSourceService: ViewSourceService,
    private navigationHistoryService: NavigationHistoryService,
    private authorisationService: AuthorisationService,
    private diagramTypeConfigService: DiagramTypeConfigService
  ) {}

  nav(id, ver, force?: boolean, extraParams: Params = {}) {
    const queryParams = {v: ver, ...extraParams};
    const extras = {
      queryParams: queryParams,
      skipLocationChange: false,
      replaceUrl: !!force
    } as NavigationExtras;

    return this.router.navigate(['diagram', id], extras);
  }

  canActivate(route: ActivatedRouteSnapshot): Promise<boolean> | boolean {
    const diagramId = route.params.diagramId;
    const state = route.queryParams.v || DIAGRAM_VERSION_STATES_ENUM.DRAFT;
    const mode = !!route.queryParams.edit;
    const jobRef = route.queryParams.copy_ref;
    const sign = route.queryParams.sign || '';
    const trainingRequestId = route.queryParams.training;
    const authorisationMode = route.queryParams.authorisation;
    const customTop = route.queryParams.top;
    const originSrc = route.queryParams.originSrc;
    const strippedMode = Boolean(typeof route.queryParams.stripped !== 'undefined');
    let permission;

    this.clientEnvSvc.setup(route.queryParams);

    if (sign) {
      this.auth.setToken(sign);
    }

    if (mode && state !== DIAGRAM_VERSION_STATES_ENUM.DRAFT) {
      // context of trainings
      const trainingModeParams = trainingRequestId ? {training: trainingRequestId} : null;

      this.nav(diagramId, state, false, trainingModeParams);

      return false;
    } else {
      const fetchDiagram$ = this.diagramStoreService.fetchDiagram(diagramId, state).pipe(
        map((data: DiagramMetaInterface) => {
          permission = data.permission;
          const nextNav = new NavObject(diagramId, state, customTop, originSrc, authorisationMode);
          const curr = this.navSvc.getCurrent();

          this.diagramTypeConfigService.setDiagramConfig(data);
          this.versionsSvc.forceDraft = false;
          this.meta.setThemes(data.themes);

          if (!nextNav.isSame(curr)) {
            this.meta.setMeta(data);
            this.diagramFollowSvc.setInitialFollow();
            this.auth.setUserData(data['user']);
            this.auth.setDiagramAccess(permission);

            return true;
          } else {
            return false;
          }
        })
      );
      let finalObservable = fetchDiagram$;

      if (jobRef) {
        finalObservable = this.fbDataSvc.subscribeForTask(jobRef).pipe(
          catchError(() => fetchDiagram$),
          map(snapshot => snapshot.payload?.val()),
          tap(value => {
            if (value?._state === 'error') {
              const popupPayload = {
                message: value.error ?? 'MAP_COPY.COPY_FAILED',
                callback: 'closeApp',
                disableClose: true
              };

              this.popupSvc.openErrorDialog(popupPayload);
            }
          }),
          filter(value => {
            const task = value ?? {status: 'done'};

            return task.status === 'done';
          }),
          switchMap(() => fetchDiagram$)
        );
      }

      return firstValueFrom(finalObservable)
        .then(isNotSameState => {
          if (isNotSameState) {
            return this.fbDataSvc.setup();
          } else {
            return this.diagramJsonSvc.get();
          }
        })
        .then(json => {
          this.navigationHistoryService.addCurrentNavItem(json);
          this.viewSourceService.setViewSource(route);
          this.toolbarStateService.initiateToolbarState(json);
          this.swimlanesSvc.setInitialVisibility(json.diagramId);
          const isPro = this.diagramJsonSvc.isPro(json);
          const isFree = this.diagramJsonSvc.isFree(json);
          const source = this.viewSourceService.getViewSource();

          this.userViewService
            .captureView({
              source,
              diagramId: json.diagramId,
              state: json.state
            })
            .subscribe();
          this.navSvc.requestPossibleNavSubject.next({diagramId, state});

          if (trainingRequestId) {
            this.trainingSvc.setCurrentId(trainingRequestId); // must happen after setup
          } else {
            this.trainingSvc.unsetCurrentId();
          }

          if (authorisationMode) {
            this.authorisationService.initAuthorisationMode(diagramId, authorisationMode);
          }

          if (mode) {
            return this.auth
              .checkDiagramPermission(diagramId, state)
              .toPromise()
              .then(() => this.fbDataSvc.lock()) // lock
              .then(() => {
                if (!this.clientEnvSvc.sidebarModuleSubject$.getValue()) {
                  this.clientEnvSvc.sidebarModuleSubject$.next(!!mode); // load module if not yet
                }
              })
              .then(() => {
                if (isPro || isFree) {
                  return this.changelogsSvc.callAutoLog(diagramId);
                }
              })
              .then(() => true);
          } else {
            return true;
          }
        })
        .catch(e => {
          const _errorStatus = _get(e, 'error.status', null);
          const navExtras: {hl?: string; top?: string; stripped?: boolean; sign?: string} = {
            ...(customTop && {top: customTop}),
            ...(strippedMode && {stripped: strippedMode}),
            ...(sign && {sign})
          };

          if (e.error === 'locked' && (e.user || e.byElements)) {
            const current = this.navSvc.getCurrent();
            const popupPayload = e.byElements
              ? {message: 'POPUPS.LOCKED_BY_ELEMENTS'}
              : {
                  message: e.byAgent ? 'POPUPS.LOCKED_BY_AGENT' : 'POPUPS.LOCKED',
                  additionalMessage: `: ${e.user}`,
                  callback: 'reloadToViewMode'
                };

            if (!current || diagramId !== current.id) {
              this.popupSvc
                .openErrorDialog(popupPayload)
                .afterClosed()
                .subscribe(() => {
                  this.nav(diagramId, state, true, navExtras);
                });

              return e;
            } else {
              this.popupSvc.openErrorDialog(popupPayload);
            }
          }

          if (e.error === 'locked' || _errorStatus === 'diagram_rights.cannot_edit') {
            const id = route.params.diagramId;
            const v = route.queryParams.v || DIAGRAM_VERSION_STATES_ENUM.DRAFT;

            this.fbDataSvc.setWithRemove(false);
            this.navSvc.setCurrent(id, v, true, customTop, originSrc, authorisationMode);
          }

          if (e.status === 0 && !mode) {
            return true; // allow transitioning to view mode on disconnect
          }

          switch (_errorStatus) {
            case ERROR_STATUSES.DIAGRAM.ERROR.VERSION_RESTRICTED: {
              if (this.versionsSvc.forceDraft) {
                this.versionsSvc.forceDraft = false;
                this.nav(diagramId, DIAGRAM_VERSION_STATES_ENUM.DRAFT, true, {
                  hl: route.queryParams.hl,
                  ...navExtras
                });
              }

              break;
            }

            case DIAGRAM_VERSION_NOT_EXIST:
              switch (true) {
                case state === DIAGRAM_VERSION_STATES_ENUM.MASTER:
                  this.nav(diagramId, DIAGRAM_VERSION_STATES_ENUM.DRAFT, true, {
                    hl: route.queryParams.hl,
                    ...navExtras
                  });
                  break;

                case !this.versionsSvc.forceDraft:
                  this.versionsSvc
                    .openVersionNotExistPopup(e.error.error)
                    .afterClosed()
                    .subscribe(navigated => {
                      if (navigated) {
                        this.versionsSvc.getVersions(diagramId); // update version tree
                      }

                      if (!this.navSvc.getCurrent() && !navigated) {
                        window.open(environment.MAIN_APP_URL, '_self');
                      }
                    });
                  break;

                default:
                  this.versionsSvc.forceDraft = false;
                  this.nav(diagramId, DIAGRAM_VERSION_STATES_ENUM.DRAFT, true, {
                    hl: route.queryParams.hl,
                    ...navExtras
                  });
                  break;
              }

              break;
          }

          return e;
        });
    }
  }
}
